<template>
  <filter-and-sort-price-slider-histogram
    v-if="!disabled"
    :price-histogram-data="priceHistogramData"
    :slider-min-price="minPrice"
    :slider-max-price="sliderMaxPrice"
    :selected-range="sliderPriceRange"
  />
  <VueSlider
    v-bind="sliderOptions"
    :min="minPrice"
    :max="sliderMaxPrice"
    :model-value="sliderPriceRange"
    :disabled="disabled"
    @dragging="updateRange"
    @change="updateRange"
  />
  <div class="filter-sort-price-slider-inputs">
    <label :class="{ disabled: disabled }">
      <translate>Minimum</translate>
      <div class="form-input">
        <template
          v-if="$i18n.locale === 'en-gb' && currencyStore.currentCurrency"
        >
          {{ currencyStore.currentCurrency.symbol }}
        </template>
        <input
          ref="min-price-input"
          type="text"
          inputmode="numeric"
          pattern="[0-9]"
          step="1"
          :value="amountToPrice(sliderPriceRange[0]).price"
          :disabled="disabled"
          @input="minPriceInputChanged"
          @blur="minPriceBlur"
        />
      </div>
      <translate v-if="sliderPriceRange[0] <= minPrice" class="no-limit-label">
        No limit set
      </translate>
    </label>
    <label :class="{ disabled: disabled }">
      <translate>Maximum</translate>
      <div class="form-input">
        <template
          v-if="$i18n.locale === 'en-gb' && currencyStore.currentCurrency"
        >
          {{ currencyStore.currentCurrency.symbol }}
        </template>
        <input
          ref="max-price-input"
          type="text"
          inputmode="numeric"
          pattern="[0-9]"
          step="1"
          :value="Math.max(1, amountToPrice(sliderPriceRange[1]).price)"
          :disabled="disabled"
          @input="maxPriceInputChanged"
          @blur="maxPriceBlur"
        />
      </div>
      <translate v-if="sliderPriceRange[1] >= maxPrice" class="no-limit-label">
        No limit set
      </translate>
    </label>
  </div>
</template>

<script setup lang="ts">
import type { PriceHistogram } from '~/apps/search/schemas'
import { Money, convertMoneyCurrency } from '~/utility/money'

const VueSlider = defineAsyncComponent(() => import('vue-slider-component'))
const currencyStore = useCurrencyStore(usePinia())

const props = defineProps<{
  minPrice: number
  maxPrice: number
  priceHistogramData: PriceHistogram
  priceRange: number[]
  disabled: boolean
}>()

const emit = defineEmits(['updateRange'])

const sliderPriceRange = ref(props.priceRange)
const sliderMaxPrice = ref(
  Math.max(
    1,
    props.priceRange[1] > props.maxPrice ? props.priceRange[1] : props.maxPrice,
  ),
)

const sliderOptions = {
  dotSize: 30,
  height: 10,
  contained: true,
  enableCross: false,
  lazy: true,
  tooltip: 'none',
  silent: true,
}

const maxInput = useTemplateRef('max-price-input')
const minInput = useTemplateRef('min-price-input')

function amountToPrice(amount) {
  const money = convertToMoney(amount)
  const price = Math.round(money.getAmount() / 100)
  const currency = money.getCurrency()
  return {
    price,
    currency,
  }
}

watch(
  () => props.maxPrice,
  () => {
    if (props.maxPrice && props.priceRange[1]) {
      if (props.priceRange[1] > props.maxPrice) {
        sliderMaxPrice.value = Math.max(1, props.priceRange[1])
      } else {
        sliderMaxPrice.value = Math.max(
          1,
          Math.ceil(props.maxPrice / 100) * 100,
        )
      }
    }
  },
)
watch(
  () => props.priceRange,
  () => {
    // A bit of rounding gets applied to sliderMaxPrice but if selected price
    // is above highest prcied campsite then it get's adjusted down to that campsites
    // price. If max price is selected use the sliderMaxPrice value.
    sliderPriceRange.value = [
      props.priceRange[0],
      props.priceRange[1] < props.maxPrice
        ? props.priceRange[1]
        : sliderMaxPrice.value,
    ]
  },
)

function minPriceInputChanged(evt) {
  let convertedAmount = convertInputValToGBP(evt.target.value)
  if (!convertedAmount) {
    // If user has typed non numeric value, remove it
    if (!!evt.target.value && minInput.value) {
      minInput.value.value = ''
    }
    return
  }

  if (convertedAmount < props.minPrice) {
    return
  }

  const newRange = [
    convertedAmount < sliderMaxPrice.value
      ? convertedAmount
      : sliderMaxPrice.value,
    sliderPriceRange.value[1],
  ]

  // If number is unchanged as a result of rounding or characters being removed
  // updateRange will not trigger update to input value to remove characters
  // so we must manually set here to keep in sync.
  // Don't sync if value 0 otherwise user gets stuck on 0 for currencies where
  // 1 unit rounds to 0 GBP.
  if (newRange[0] !== 0 && newRange[0] === sliderPriceRange.value[0]) {
    sliderPriceRange.value = [...sliderPriceRange.value]
  }

  updateRange(newRange)
}

function minPriceBlur(evt) {
  const convertedAmount = convertInputValToGBP(evt.target.value) || 0

  const newRange = [
    convertedAmount < sliderMaxPrice.value
      ? convertedAmount
      : sliderMaxPrice.value,
    sliderPriceRange.value[1],
  ]

  // We need to allow a user to enter a value into the text field less than the
  // min value in input event handler for when they are part way though typing
  // number. If they leave number less than min catch it here.
  if (newRange[0] < props.minPrice) {
    sliderPriceRange.value = [props.minPrice, newRange[1]]
    updateRange(sliderPriceRange.value)
    return
  }

  // Sync for currencies where 1 unit rounds to 0 GBP which is prevented on input
  if (newRange[0] === sliderPriceRange.value[0]) {
    sliderPriceRange.value = [...sliderPriceRange.value]
  }

  if (sliderPriceRange.value[0] > sliderPriceRange.value[1]) {
    const newRange = [props.minPrice, sliderPriceRange.value[1]]
    updateRange(newRange)
  }
}

function maxPriceInputChanged(evt) {
  const convertedAmount = convertInputValToGBP(evt.target.value)
  if (!convertedAmount) {
    // If user has typed non numeric value, remove it
    if (!!evt.target.value && maxInput.value) {
      maxInput.value.value = ''
    }
    return
  }

  const newRange = [
    sliderPriceRange.value[0],
    convertedAmount < sliderMaxPrice.value
      ? convertedAmount
      : sliderMaxPrice.value,
  ]

  // If number is unchanged as a result of rounding or characters being removed
  // updateRange will not trigger update to input value to remove characters
  // so we must manually set here to keep in sync
  if (newRange[1] === sliderPriceRange.value[1]) {
    sliderPriceRange.value = [...sliderPriceRange.value]
  }

  if (newRange[1] > 0) {
    updateRange(newRange)
  }
}

function maxPriceBlur(evt) {
  const convertedAmount = convertInputValToGBP(evt.target.value)

  if (
    convertedAmount === 0 ||
    sliderPriceRange.value[1] < sliderPriceRange.value[0]
  ) {
    const newRange = [
      sliderPriceRange.value[0],
      Math.max(1, sliderPriceRange.value[0] + 100),
    ]
    updateRange(newRange)
  }
}

function convertInputValToGBP(val) {
  let newVal = val
  if (newVal.match(/[^0-9.]/g)) {
    newVal = newVal.replace(/[^0-9]/g, '')
  }
  if (newVal.match(/\./g)) {
    newVal = Math.round(Number(newVal))
    sliderPriceRange.value = [...sliderPriceRange.value]
  }

  if (!newVal) {
    return
  }

  const converted = convertLocaleToGB(Number(newVal) * 100)
  const convertedAmount = converted.getAmount()

  return convertedAmount
}

function convertToMoney(amount = 0) {
  const roundedAmount = Math.round(amount)
  if (currencyFrom.value && currencyTo.value && !isNaN(roundedAmount)) {
    return convertMoneyCurrency(
      Money({
        amount: roundedAmount,
        currency: 'GBP',
      }),
      currencyFrom.value,
      currencyTo.value,
    )
  } else {
    return Money({ amount: 0, currency: 'GBP' })
  }
}

function convertLocaleToGB(amount = 0) {
  if (currencyFrom.value && currencyTo.value) {
    return convertMoneyCurrency(
      Money({
        amount,
        currency: 'USD',
      }),
      currencyTo.value,
      currencyFrom.value,
    )
  } else {
    return Money({ amount: 0, currency: 'GBP' })
  }
}

const currencyFrom = computed(() => {
  return currencyStore.currencies.GBP
})

const currencyTo = computed(() => {
  return currencyStore.currentCurrency
})

function updateRange(range: number[]) {
  if (range[0] <= props.minPrice) {
    delete range[0]
  }
  if (range[1] >= props.maxPrice) {
    delete range[1]
  }
  emit('updateRange', range)
}
</script>
<style scoped>
.filter-sort-price-slider-inputs {
  display: flex;
  column-gap: 3em;
  justify-content: space-between;
  margin: 0.75em auto;
}

label {
  font-weight: normal;
}

label.disabled {
  color: #bbb;
  pointer-events: none;
  cursor: not-allowed;
  input {
    color: #bbb;
  }
}

.filter-sort-price-slider-inputs div {
  display: flex;
  align-items: baseline;
}

.filter-sort-price-slider-inputs input {
  width: 100%;
  border: none;
  padding: 0;
  font-size: 1em;
  margin-left: 0.2em;
}

.no-limit-label {
  font-size: 0.8em;
}
</style>
